import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Sparkles, CheckCircle2, XCircle } from "lucide-react";

export default function UnsubscribePage() {
  const [params] = useSearchParams();
  const campaign = params.get("campaign") || "";
  const contact = params.get("contact") || "";
  const [info, setInfo] = useState(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!contact) return;
    api.get(`/track/unsubscribe/info?campaign=${campaign}&contact=${contact}`)
      .then((r) => setInfo(r.data))
      .catch(() => setInfo({ email: null }));
  }, [campaign, contact]);

  const submit = async () => {
    setSubmitting(true);
    setError("");
    try {
      const { data } = await api.post("/track/unsubscribe", { campaign, contact });
      if (data.ok) setDone(true);
      else setError(data.detail || "Failed");
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center px-4" data-testid="unsubscribe-page">
      <div className="bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 rounded-sm max-w-md w-full p-10">
        <div className="flex items-center gap-2 mb-8">
          <div className="h-7 w-7 rounded-sm flex items-center justify-center" style={{ backgroundColor: "var(--brand-primary)" }}>
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <span className="font-display font-black tracking-tight">Mailwave</span>
        </div>

        {!contact ? (
          <>
            <XCircle className="h-10 w-10 text-red-600 mb-4" />
            <h1 className="font-display font-black text-3xl tracking-tighter mb-2">Invalid link</h1>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">This unsubscribe link is missing required parameters.</p>
          </>
        ) : done || info?.status === "unsubscribed" ? (
          <>
            <CheckCircle2 className="h-10 w-10 mb-4" style={{ color: "var(--accent-green)" }} />
            <h1 className="font-display font-black text-3xl tracking-tighter mb-3">You're unsubscribed.</h1>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">
              {info?.email && <><span className="font-semibold text-neutral-900 dark:text-neutral-50">{info.email}</span> </>}
              has been removed from this list. You won't receive further emails about this campaign.
            </p>
          </>
        ) : (
          <>
            <p className="label-overline mb-3">Confirm</p>
            <h1 className="font-display font-black text-3xl tracking-tighter mb-3">Unsubscribe?</h1>
            <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-8">
              {info?.email ? <>We'll stop sending emails to <span className="font-semibold text-neutral-900 dark:text-neutral-50">{info.email}</span>.</> : "Click below to stop receiving these emails."}
            </p>
            <Button onClick={submit} disabled={submitting} className="w-full h-11 rounded-sm btn-brand font-semibold" data-testid="unsubscribe-confirm-button">
              {submitting ? "Unsubscribing..." : "Unsubscribe me"}
            </Button>
            {error && <p className="text-sm text-red-600 mt-3">{error}</p>}
          </>
        )}
      </div>
    </div>
  );
}
